import { Alert, Badge, Button, Collapse, Table } from "react-bootstrap";
import { useQuery } from "react-query";
import { useAuth } from "../../hooks/useAuth";
import { fetchActionsUtil } from "../../utils/helpers";
import { useState } from "react";
import { EditIcon } from "../Icons";
import EditAccounts from "../modals/EditAccounts";
import { Link } from "react-router-dom";
import currency from "currency.js";
import CachedIcon from "mdi-react/CachedIcon";

function IncorrectAccountsData() {
  const { backendUrl } = useAuth();
  const [openSections, setOpenSections] = useState({
    accounts: false,
    duplicates: false,
    unmatched: false,
  });
  const [selectedAccountToEdit, setSelectedAccountToEdit] = useState();

  const toggleSection = section =>
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));

  const fetchInvalidAccounts = async () => {
    const response = await fetchActionsUtil(
      `${backendUrl}/api/accounts/validation/invalid-account-lists`,
      "GET"
    );
    return {
      accounts: response?.data?.accounts || [],
      duplicateDescriptions: response?.data?.duplicateDescriptions || [],
      unmatchedAccounts: response?.data?.unmatchedAccounts || [],
      missingSystemAccounts: response?.data?.missingSystemAccounts || [],
    };
  };

  const {
    data = {
      accounts: [],
      duplicateDescriptions: [],
      unmatchedAccounts: [],
      missingSystemAccounts: [],
    },
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery(["INVALID_ACCOUNT_LIST"], fetchInvalidAccounts, {
    enabled: !!backendUrl,
    keepPreviousData: true,
  });

  const {
    accounts,
    duplicateDescriptions,
    unmatchedAccounts,
    missingSystemAccounts,
  } = data;

  const hasIssues =
    accounts.length ||
    duplicateDescriptions.length ||
    unmatchedAccounts.length ||
    missingSystemAccounts.length;

  if (isLoading || isFetching || isError || !hasIssues) return null;

  return (
    <>
      <Alert variant="warning" className="m-3 mb-0">
        <Alert.Heading>
          Account Validation Issues Detected{" "}
          <Button variant="" onClick={() => refetch()}>
            <CachedIcon />{" "}
          </Button>{" "}
        </Alert.Heading>
        <p className="mb-3 text-muted" style={{ fontSize: "0.9rem" }}>
          The following issues were found in your account data. Please review
          and resolve them.
        </p>

        {/* ── INVALID ACCOUNTS ── */}
        {accounts.length > 0 && (
          <div className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-1">
              <strong>Incomplete / Invalid Accounts</strong>
              <Badge bg="warning" text="dark">
                {accounts.length}
              </Badge>
              <button
                className="btn btn-link p-0 ms-1"
                style={{ fontSize: "0.85rem" }}
                onClick={() => toggleSection("accounts")}
                aria-controls="collapse-accounts"
                aria-expanded={openSections.accounts}
              >
                {openSections.accounts ? "Hide" : "Show"}
              </button>
            </div>
            <Collapse in={openSections.accounts}>
              <div id="collapse-accounts" className="table-responsive">
                <Table
                  borderless
                  striped
                  hover
                  size="sm"
                  className="mb-0 product-table"
                >
                  <thead>
                    <tr>
                      <th />
                      <th>#</th>
                      <th>AccountID</th>
                      <th>Description</th>
                      <th>Account Type</th>
                      <th>Sub Account</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((account, index) => (
                      <tr key={account?.AccountID || index}>
                        <td>
                          <Button
                            variant=""
                            onClick={() =>
                              setSelectedAccountToEdit({
                                ...account,
                                Account_Desc:
                                  account.Account_Desc || account.Description,
                                Type: account.Type?.toUpperCase(),
                              })
                            }
                          >
                            <EditIcon />
                          </Button>
                        </td>
                        <td>{index + 1}</td>
                        <td>{account?.AccountID || "—"}</td>
                        <td>{account?.Description || "—"}</td>
                        <td>{account?.Type || "—"}</td>
                        <td>{account?.DetailType || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Collapse>
          </div>
        )}

        {/* ── DUPLICATE DESCRIPTIONS ── */}
        {duplicateDescriptions.length > 0 && (
          <div className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-1">
              <strong>Duplicate Descriptions</strong>
              <Badge bg="danger">{duplicateDescriptions.length}</Badge>
              <button
                className="btn btn-link p-0 ms-1"
                style={{ fontSize: "0.85rem" }}
                onClick={() => toggleSection("duplicates")}
                aria-controls="collapse-duplicates"
                aria-expanded={openSections.duplicates}
              >
                {openSections.duplicates ? "Hide" : "Show"}
              </button>
            </div>
            <Collapse in={openSections.duplicates}>
              <div id="collapse-duplicates" className="table-responsive">
                <Table
                  borderless
                  striped
                  hover
                  size="sm"
                  className="mb-0 product-table"
                >
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Description</th>
                      <th>Duplicate Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {duplicateDescriptions.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item?.Description || "—"}</td>
                        <td>
                          <Badge bg="danger">{item?.DuplicateCount}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Collapse>
          </div>
        )}

        {/* ── UNMATCHED JOURNAL ACCOUNTS ── */}
        {unmatchedAccounts.length > 0 && (
          <div className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-1">
              <strong>Unmatched Journal Entries</strong>
              <Badge bg="light">{unmatchedAccounts.length}</Badge>
              <button
                className="btn btn-link p-0 ms-1"
                style={{ fontSize: "0.85rem" }}
                onClick={() => toggleSection("unmatched")}
                aria-controls="collapse-unmatched"
                aria-expanded={openSections.unmatched}
              >
                {openSections.unmatched ? "Hide" : "Show"}
              </button>
            </div>
            <Collapse in={openSections.unmatched}>
              <div id="collapse-unmatched" className="table-responsive">
                <Table
                  borderless
                  striped
                  hover
                  size="sm"
                  className="mb-0 product-table"
                >
                  <thead>
                    <tr>
                      <th />
                      <th>#</th>
                      <th>AccountID</th>
                      <th>Description</th>
                      <th>Debit</th>
                      <th>Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatchedAccounts.map((entry, index) => (
                      <tr key={index}>
                        <td>
                          <Link to={`/journal-entry/edit/${entry.TransID}`}>
                            <EditIcon />
                          </Link>
                        </td>
                        <td>{index + 1}</td>
                        <td>{entry?.AccountID || "—"}</td>
                        <td>{entry?.Description || "—"}</td>
                        <td>
                          {currency(entry?.Debit, {
                            symbol: "",
                          }).format() ?? "—"}
                        </td>
                        <td>
                          {currency(entry?.Credit, {
                            symbol: "",
                          }).format() ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Collapse>
          </div>
        )}

        {/* ── MISSING SYSTEM ACCOUNTS ── */}
        {missingSystemAccounts.length > 0 && (
          <div className="mb-1">
            <div className="d-flex align-items-center gap-2 mb-1">
              <strong>Missing System Accounts</strong>
              <Badge bg="warning" text="dark">
                {missingSystemAccounts.length}
              </Badge>
              <button
                className="btn btn-link p-0 ms-1"
                style={{ fontSize: "0.85rem" }}
                onClick={() => toggleSection("missingSystemAccounts")}
                aria-controls="collapse-missingSystemAccounts"
                aria-expanded={openSections.missingSystemAccounts}
              >
                {openSections.missingSystemAccounts ? "Hide" : "Show"}
              </button>
            </div>
            <Collapse in={openSections.missingSystemAccounts}>
              <div
                id="collapse-missingSystemAccounts"
                className="table-responsive"
              >
                <Table
                  borderless
                  striped
                  hover
                  size="sm"
                  className="mb-0 product-table"
                >
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Description</th>
                      <th>Account Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missingSystemAccounts.map((account, index) => (
                      <tr key={account?.AccountID || index}>
                        <td>{index + 1}</td>
                        <td>{account?.Description || "—"}</td>
                        <td>{account?.Type || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Collapse>
          </div>
        )}
      </Alert>

      {selectedAccountToEdit && (
        <EditAccounts
          selectedAccountToEdit={selectedAccountToEdit}
          show={!!selectedAccountToEdit}
          onHide={() => setSelectedAccountToEdit()}
          refetch={refetch}
        />
      )}
    </>
  );
}

export default IncorrectAccountsData;
